<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-content">
      <div class="modal-header">
        <div class="header-content">
          <div class="icon-wrapper">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" fill="currentColor"/>
            </svg>
          </div>
          <div>
            <h2>Edit Profile</h2>
            <p>Update your profile information and settings</p>
          </div>
        </div>
        <button class="close-btn" @click="$emit('close')" aria-label="Close modal">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
      </div>
      
      <div class="form-container">
        <form @submit.prevent="submitForm">
          <div class="form-grid">
            <!-- Profile Picture Section -->
            <div class="input-group full-width">
              <label>Profile Picture</label>
              <div class="profile-picture-section">
                <div class="current-picture" :class="{ 'uploading': isUploadingImage }">
                  <img 
                    :src="profileImagePreview || currentProfileData.profilepic || require('@/assets/profile.png')" 
                    alt="Current profile picture" 
                    class="profile-preview"
                  />
                  <div v-if="isUploadingImage" class="upload-loading-overlay">
                    <div class="spinner-container">
                      <div class="modern-spinner">
                        <div class="spinner-ring"></div>
                        <div class="spinner-ring"></div>
                        <div class="spinner-ring"></div>
                      </div>
                      <p class="uploading-text">Uploading...</p>
                    </div>
                  </div>
                  <div v-else class="picture-overlay">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1H5C3.89 1 3 1.89 3 3V21C3 22.11 3.89 23 5 23H19C20.11 23 21 22.11 21 21V9M19 9H14V4H5V21H19V9Z" fill="currentColor"/>
                    </svg>
                  </div>
                </div>
                <div class="picture-meta">
                  <p class="picture-title">
                    {{ (form.profilepic || currentProfileData.profilepic) ? 'Edit Profile Photo' : 'Upload Profile Pic' }}
                  </p>
                  <button 
                    type="button" 
                    class="upload-link-btn"
                    @click="triggerFileInput"
                    :disabled="isSubmitting"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                    {{ (form.profilepic || currentProfileData.profilepic) ? 'Change Photo' : 'Upload Photo' }}
                  </button>
                </div>
                <input 
                  type="file" 
                  ref="fileInput"
                  @change="handleImageUpload" 
                  accept="image/*"
                  class="file-input"
                  :disabled="isSubmitting"
                />
              </div>
            </div>

            <!-- Name Field -->
            <div class="input-group">
              <label for="providerAdminName">Full Name <span class="required">*</span></label>
              <div class="input-wrapper">
                <svg class="input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" fill="currentColor"/>
                </svg>
                <input 
                  type="text" 
                  id="providerAdminName" 
                  v-model="form.providerAdminName" 
                  required
                  placeholder="Enter your full name"
                  :disabled="isSubmitting"
                />
              </div>
            </div>

            <!-- Mobile Number Field -->
            <div class="input-group">
              <label for="mobileno">Mobile Number <span class="required">*</span></label>
              <div class="input-wrapper">
                <svg class="input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" fill="currentColor"/>
                </svg>
                <input 
                  type="tel" 
                  id="mobileno" 
                  v-model="form.mobileno" 
                  required
                  placeholder="Enter mobile number with country code"
                  :disabled="isSubmitting"
                />
              </div>
            </div>

            <!-- Email Field (read-only) -->
            <div class="input-group">
              <label for="email">Email</label>
              <div class="input-wrapper">
                <svg class="input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.89 2 1.99 2H20c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z" fill="currentColor"/>
                </svg>
                <input
                  type="email"
                  id="email"
                  v-model="form.email"
                  placeholder="Email not available"
                  disabled
                />
              </div>
            </div>
          </div>

          <div class="form-actions">
            <button type="button" class="btn-secondary" @click="$emit('close')" :disabled="isSubmitting">
              Cancel
            </button>
            <button type="submit" class="btn-primary" :disabled="isSubmitting">
              <span v-if="!isSubmitting">Update Profile</span>
              <span v-else class="loading-content">
                <div class="button-spinner"></div>
                Updating...
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Toast Notifications -->
    <div class="toast-container">
      <transition-group name="toast" tag="div">
        <div 
          v-for="toast in toasts" 
          :key="toast.id" 
          class="toast"
          :class="[`toast-${toast.type}`]"
        >
          <div class="toast-icon">
            <svg v-if="toast.type === 'error'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
            <svg v-else-if="toast.type === 'success'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22,4 12,14.01 9,11.01"/>
            </svg>
            <svg v-else-if="toast.type === 'info'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="16" x2="12" y2="12"/>
              <line x1="12" y1="8" x2="12.01" y2="8"/>
            </svg>
            <svg v-else-if="toast.type === 'warning'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          </div>
          <div class="toast-content">
            <p class="toast-message">{{ toast.message }}</p>
          </div>
          <button class="toast-close" @click="removeToast(toast.id)">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      </transition-group>
    </div>
  </div>
</template>

<script>
import apiService from '@/services/api';
import { isNoDataError, isHmsSubscriptionInactiveError } from '../utilities/errorHelper';
import { readSessionToken } from '@/utilities/sessionTokenStorage';

export default {
  name: 'EditProfile',
  props: {
    adminProfileData: {
      type: Object,
      required: true,
    }
  },
  data() {
    return {
      isSubmitting: false,
      currentProfileData: {},
      form: {
        providerAdminName: '',
        mobileno: '',
        email: '',
        profilepic: ''
      },
      profileImagePreview: null,
      uploadedImageUrl: null,
      toasts: [],
      maxFileSize: 5 * 1024 * 1024, // 5MB
      allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      isUploadingImage: false
    };
  },
  mounted() {
    this.initializeForm();
  },
  methods: {
    // Toast notification methods
    showToast(message, type = 'error', duration = 5000) {
      const id = Date.now() + Math.random();
      this.toasts.push({ id, message, type });
      
      if (duration > 0) {
        setTimeout(() => {
          this.removeToast(id);
        }, duration);
      }
    },
    removeToast(id) {
      this.toasts = this.toasts.filter(toast => toast.id !== id);
    },

    // Initialize form with current data
    initializeForm() {
      this.currentProfileData = { ...this.adminProfileData };
      this.form.providerAdminName = this.adminProfileData.providerAdminName || '';
      this.form.mobileno = this.adminProfileData.mobileno || '';
      this.form.email = this.adminProfileData.email || '';
      this.form.profilepic = this.adminProfileData.profilepic || '';
    },

    // File input handling
    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    // Image upload handling
    async handleImageUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      // Check file size (5MB limit)
      if (file.size > this.maxFileSize) {
        this.showToast('Image size exceeds 5MB limit. Please select a smaller image.', 'error');
        event.target.value = '';
        return;
      }
      
      if (this.validateFile(file)) {
        try {
          const normalizedFile = await this.normalizeImageForUpload(file);
          this.uploadImage(normalizedFile);
        } catch (error) {
          console.error('Image normalization failed:', error);
          this.showToast('Failed to process image orientation. Please try another image.', 'error');
        }
      } else {
        this.showToast('Invalid file type. Please upload a valid image file (JPEG, PNG, GIF, or WebP) under 5MB.', 'error');
        event.target.value = '';
      }
    },

    // File validation
    validateFile(file) {
      return this.allowedTypes.includes(file.type) && file.size <= this.maxFileSize;
    },

    async normalizeImageForUpload(file) {
      // GIF/WebP are kept as-is to avoid breaking animation frames.
      if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
        return file;
      }

      const orientation = await this.getExifOrientation(file);
      if (!orientation || orientation === 1) {
        return file;
      }

      const normalizedBlob = await this.renderOrientedImage(file, orientation);
      return new File([normalizedBlob], file.name, {
        type: file.type,
        lastModified: Date.now()
      });
    },

    async getExifOrientation(file) {
      // EXIF orientation is relevant for JPEGs. For other formats, return 1.
      if (!['image/jpeg', 'image/jpg'].includes(file.type)) {
        return 1;
      }

      const buffer = await file.arrayBuffer();
      const view = new DataView(buffer);

      // JPEG SOI marker check.
      if (view.getUint16(0, false) !== 0xFFD8) {
        return 1;
      }

      let offset = 2;
      while (offset < view.byteLength) {
        const marker = view.getUint16(offset, false);
        offset += 2;

        // APP1 marker where EXIF usually lives.
        if (marker === 0xFFE1) {
          view.getUint16(offset, false);
          offset += 2;

          // "Exif\0\0"
          if (view.getUint32(offset, false) !== 0x45786966) {
            return 1;
          }

          const tiffOffset = offset + 6;
          const little = view.getUint16(tiffOffset, false) === 0x4949;
          const firstIfdOffset = view.getUint32(tiffOffset + 4, little);
          let ifdOffset = tiffOffset + firstIfdOffset;
          const entries = view.getUint16(ifdOffset, little);
          ifdOffset += 2;

          for (let i = 0; i < entries; i += 1) {
            const entryOffset = ifdOffset + i * 12;
            const tag = view.getUint16(entryOffset, little);
            if (tag === 0x0112) {
              return view.getUint16(entryOffset + 8, little);
            }
          }

          return 1;
        }

        // Not an APP1 block, skip block.
        if ((marker & 0xFF00) !== 0xFF00) {
          break;
        }
        const size = view.getUint16(offset, false);
        offset += size;
      }

      return 1;
    },

    async renderOrientedImage(file, orientation) {
      const imageBitmap = await createImageBitmap(file);
      const sourceWidth = imageBitmap.width;
      const sourceHeight = imageBitmap.height;
      const isSwapNeeded = [5, 6, 7, 8].includes(orientation);

      const canvas = document.createElement('canvas');
      canvas.width = isSwapNeeded ? sourceHeight : sourceWidth;
      canvas.height = isSwapNeeded ? sourceWidth : sourceHeight;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Unable to get canvas context');
      }

      switch (orientation) {
        case 2:
          ctx.translate(canvas.width, 0);
          ctx.scale(-1, 1);
          break;
        case 3:
          ctx.translate(canvas.width, canvas.height);
          ctx.rotate(Math.PI);
          break;
        case 4:
          ctx.translate(0, canvas.height);
          ctx.scale(1, -1);
          break;
        case 5:
          ctx.rotate(0.5 * Math.PI);
          ctx.scale(1, -1);
          break;
        case 6:
          ctx.rotate(0.5 * Math.PI);
          ctx.translate(0, -canvas.width);
          break;
        case 7:
          ctx.rotate(0.5 * Math.PI);
          ctx.translate(canvas.height, -canvas.width);
          ctx.scale(-1, 1);
          break;
        case 8:
          ctx.rotate(-0.5 * Math.PI);
          ctx.translate(-canvas.height, 0);
          break;
        default:
          break;
      }

      ctx.drawImage(imageBitmap, 0, 0, sourceWidth, sourceHeight);

      const outputType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
      const quality = outputType === 'image/jpeg' ? 0.92 : undefined;

      const blob = await new Promise((resolve) => {
        canvas.toBlob(resolve, outputType, quality);
      });

      if (!blob) {
        throw new Error('Failed to create normalized image blob');
      }

      imageBitmap.close();
      return blob;
    },

    // Upload image to server
    async uploadImage(file) {
      try {
        this.isUploadingImage = true;
        const formData = new FormData();
        formData.append('files', file);

        const uploadResponse = await apiService.uploadImage(formData);

        const uploadedFilenames = uploadResponse.data || [];
        if (uploadedFilenames.length > 0) {
          // Store just the filename
          const imageFilename = uploadedFilenames[0];
          this.uploadedImageUrl = imageFilename;
          this.form.profilepic = imageFilename;
          
          // Create preview
          const reader = new FileReader();
          reader.onload = e => {
            this.profileImagePreview = e.target.result;
          };
          reader.readAsDataURL(file);

          // Auto-save profile picture immediately after upload
          await this.autoUpdateProfilePicture();
        } else {
          throw new Error('No image URL received from server');
        }
      } catch (error) {
        console.error('Image upload failed:', error);
        this.showToast('Failed to upload image. Please try again.', 'error');
      } finally {
        this.isUploadingImage = false;
      }
    },

    async autoUpdateProfilePicture() {
      const payload = {
        providerAdminName: this.form.providerAdminName.trim(),
        mobileno: this.form.mobileno.trim(),
        profilepic: this.form.profilepic || this.currentProfileData.profilepic
      };

      const response = await apiService.updateProviderAdmin(payload);
      if (!response.data) {
        throw new Error('Invalid response from server');
      }

      try {
        const getResponse = await apiService.getProviderAdmin();
        const updatedProfileData = getResponse.data || {
          ...this.currentProfileData,
          ...payload
        };
        localStorage.setItem('ADMIN_PROFILE_DATA', JSON.stringify(updatedProfileData));
        this.currentProfileData = { ...updatedProfileData };
        this.$emit('profile-updated', updatedProfileData);
      } catch (fetchError) {
        const updatedProfileData = {
          ...this.currentProfileData,
          ...payload
        };
        localStorage.setItem('ADMIN_PROFILE_DATA', JSON.stringify(updatedProfileData));
        this.currentProfileData = { ...updatedProfileData };
        this.$emit('profile-updated', updatedProfileData);
      }

      this.showToast('Profile picture updated successfully!', 'success', 3000);
    },

    // Get authentication token
    getAuthToken() {
      try {
        const token = readSessionToken();
        if (!token) {
          throw new Error('No authentication token found');
        }
        return token;
      } catch (error) {
        console.error('Error getting auth token:', error);
        this.showToast('Authentication error. Please login again.', 'error');
        setTimeout(() => {
          this.$router.push('/login');
        }, 2000);
        throw error;
      }
    },

    // Form validation
    validateForm() {
      if (!this.form.providerAdminName.trim()) {
        this.showToast('Please enter your full name.', 'warning');
        return false;
      }
      if (!this.form.mobileno.trim()) {
        this.showToast('Please enter your mobile number.', 'warning');
        return false;
      }
      return true;
    },

    // Submit form
    async submitForm() {
      if (!this.validateForm()) {
        return;
      }

      try {
        this.isSubmitting = true;
        
        const payload = {
          providerAdminName: this.form.providerAdminName.trim(),
          mobileno: this.form.mobileno.trim(),
          profilepic: this.form.profilepic || this.currentProfileData.profilepic
        };

        const response = await apiService.updateProviderAdmin(payload);

        if (response.data) {
          // Fetch updated profile data from GET API to get full image URL
          try {
            const getResponse = await apiService.getProviderAdmin();

            if (getResponse.data) {
              // Update localStorage with complete data including full image URL
              localStorage.setItem('ADMIN_PROFILE_DATA', JSON.stringify(getResponse.data));
              
              this.showToast('Profile updated successfully!', 'success', 3000);
              
              // Emit event to parent component with full profile data
              this.$emit('profile-updated', getResponse.data);
              
              setTimeout(() => {
                this.$emit('close');
              }, 1500);
            } else {
              // Fallback: use payload data if GET fails
              const updatedProfileData = {
                ...this.currentProfileData,
                ...payload
              };
              localStorage.setItem('ADMIN_PROFILE_DATA', JSON.stringify(updatedProfileData));
              this.showToast('Profile updated successfully!', 'success', 3000);
              this.$emit('profile-updated', updatedProfileData);
              setTimeout(() => {
                this.$emit('close');
              }, 1500);
            }
          } catch (fetchError) {
            console.error('Failed to fetch updated profile:', fetchError);
            // Fallback: use payload data if GET fails
            const updatedProfileData = {
              ...this.currentProfileData,
              ...payload
            };
            localStorage.setItem('ADMIN_PROFILE_DATA', JSON.stringify(updatedProfileData));
            this.showToast('Profile updated successfully!', 'success', 3000);
            this.$emit('profile-updated', updatedProfileData);
            setTimeout(() => {
              this.$emit('close');
            }, 1500);
          }
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (error) {
        console.error('Profile update failed:', error);
        this.handleApiError(error, 'Updating profile');
      } finally {
        this.isSubmitting = false;
      }
    },

    // Enhanced API error handling
    handleApiError(error, context = 'Operation') {
      // Skip error handling for "no data" cases (status 400 with errorcode 2003)
      if (isNoDataError(error) || isHmsSubscriptionInactiveError(error)) {
        return;
      }

      console.error(`${context} error:`, error);
      
      let message = null;
      let type = 'error';

      if (error?.response?.data) {
        const errorData = error.response.data;
        const status = error.response.status;
        
        // For HTTP 400 errors, always use the reason field from API response
        if (status === 400) {
          if (errorData.reason) {
            message = errorData.reason;
          }
          // If no reason provided for 400 error, don't show toast (no generic messages)
        } else {
          // For other status codes, prioritize reason field
          if (errorData.reason) {
            message = errorData.reason;
          } else if (errorData.message) {
            message = errorData.message;
          } else {
            // Fallback messages for other status codes only if no reason/message
            switch (status) {
              case 401:
                message = 'Session expired. Please login again.';
                setTimeout(() => {
                  this.$router.push('/login');
                }, 2000);
                break;
              case 403:
                message = 'You do not have permission to perform this action.';
                break;
              case 404:
                message = 'The requested resource was not found.';
                break;
              case 429:
                message = 'Too many requests. Please wait a moment and try again.';
                break;
              case 500:
                message = 'Server error occurred. Please try again later.';
                break;
              case 503:
                message = 'Service temporarily unavailable. Please try again later.';
                break;
            }
          }
        }
      } else if (error?.request) {
        message = 'No response from server. Please check your internet connection.';
      } else if (error?.message) {
        message = error.message;
      }

      // Only show toast if we have a message (genuine errors only)
      if (message) {
        this.showToast(message, type);
      }
    }
  }
};
</script>

<style scoped>
/* Modal Overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 80px;
  z-index: 9999;
  animation: fadeIn 0.2s ease-out;
  overflow-y: auto;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Modal Content */
.modal-content {
  background: #ffffff;
  border-radius: 16px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  animation: slideUp 0.3s ease-out;
  position: relative;
  display: grid;
  grid-template-rows: auto 1fr;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Modal Header */
.modal-header {
  background: linear-gradient(135deg, #5b2e8e 0%, #7c3aed 100%);
  color: white;
  padding: 24px 32px;
  position: relative;
  overflow: hidden;
}

.modal-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="25" cy="25" r="1" fill="rgba(255,255,255,0.1)"/><circle cx="75" cy="75" r="1" fill="rgba(255,255,255,0.1)"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
  opacity: 0.3;
  pointer-events: none;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
  position: relative;
  z-index: 1;
}

.header-content > div:last-child {
  text-align: left;
}

.icon-wrapper {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-header h2 {
  margin: 0 0 4px 0;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
  text-align: left;
}

.modal-header p {
  margin: 0;
  font-size: 14px;
  opacity: 0.9;
  font-weight: 400;
  text-align: left;
}

.close-btn {
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 8px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: white;
  z-index: 2;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.05);
}

/* Form Container */
.form-container {
  padding: 32px;
  overflow-y: auto;
  max-height: calc(90vh - 120px);
}

/* Form Grid */
.form-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  margin-bottom: 32px;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.full-width {
  grid-column: span 1;
}

/* Labels */
label {
  font-weight: 600;
  font-size: 14px;
  color: #374151;
  line-height: 1.4;
  text-align: left;
  display: block;
}

.required {
  color: #ef4444;
  font-size: 12px;
  margin-left: 2px;
}

/* Input Wrapper */
.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 12px;
  color: #6b7280;
  z-index: 1;
  pointer-events: none;
}

/* Input Styles */
input {
  width: 100%;
  padding: 14px 16px 14px 44px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 16px;
  background: #ffffff;
  transition: all 0.2s ease;
  box-sizing: border-box;
}

input:focus {
  outline: none;
  border-color: #5b2e8e;
  box-shadow: 0 0 0 3px rgba(91, 46, 142, 0.1);
}

input:disabled {
  background: #f9fafb;
  color: #6b7280;
  cursor: not-allowed;
}

input::placeholder {
  color: #9ca3af;
  font-size: 14px;
}

/* Profile Picture Section */
.profile-picture-section {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 12px;
  border: 2px dashed #d1d5db;
  transition: all 0.2s ease;
}

.profile-picture-section:hover {
  border-color: #5b2e8e;
  background: #f3f4f6;
}

.current-picture {
  position: relative;
  width: 88px;
  height: 88px;
  border-radius: 50%;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.current-picture:hover {
  transform: scale(1.02);
}

.profile-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border: 3px solid #e5e7eb;
  border-radius: 50%;
}

.picture-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: white;
  opacity: 0;
  transition: all 0.2s ease;
  border-radius: 50%;
}

.current-picture:hover .picture-overlay {
  opacity: 1;
}

.picture-overlay svg {
  width: 24px;
  height: 24px;
}

.picture-overlay span {
  display: none;
}

.upload-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  z-index: 10;
  animation: fadeInOverlay 0.3s ease-out;
  padding: 10px;
  box-sizing: border-box;
}

@keyframes fadeInOverlay {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.spinner-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 6px;
  text-align: center;
}

/* Modern Professional Spinner */
.modern-spinner {
  position: relative;
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.spinner-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 3px solid transparent;
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
}

.spinner-ring:nth-child(1) {
  width: 56px;
  height: 56px;
  border-top-color: #ffffff;
  border-right-color: rgba(255, 255, 255, 0.3);
  animation-duration: 1.2s;
  animation-timing-function: cubic-bezier(0.5, 0, 0.5, 1);
}

.spinner-ring:nth-child(2) {
  width: 44px;
  height: 44px;
  border-top-color: #a78bfa;
  border-right-color: rgba(167, 139, 250, 0.3);
  animation-duration: 1s;
  animation-timing-function: cubic-bezier(0.5, 0, 0.5, 1);
  animation-direction: reverse;
  top: 6px;
  left: 6px;
}

.spinner-ring:nth-child(3) {
  width: 32px;
  height: 32px;
  border-top-color: #7c3aed;
  border-right-color: rgba(124, 58, 237, 0.3);
  animation-duration: 0.8s;
  animation-timing-function: cubic-bezier(0.5, 0, 0.5, 1);
  top: 12px;
  left: 12px;
}

@keyframes spin-ring {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.uploading-text {
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  margin: 0;
  letter-spacing: 0.5px;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  animation: pulse-text 2s ease-in-out infinite;
}

@keyframes pulse-text {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

.current-picture.uploading {
  pointer-events: none;
}

.current-picture.uploading .profile-preview {
  opacity: 0.6;
  filter: blur(2px);
  transition: opacity 0.3s ease, filter 0.3s ease;
}

.file-input {
  display: none;
}

.picture-meta {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-start;
}

.picture-title {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}

.upload-link-btn {
  border: none;
  background: #ede9fe;
  color: #5b2e8e;
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.upload-link-btn:hover:not(:disabled) {
  background: #ddd6fe;
}

.upload-link-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Button Styles */
.btn-primary {
  background: linear-gradient(135deg, #5b2e8e 0%, #7c3aed 100%);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 14px 28px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 48px;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 10px 25px -5px rgba(91, 46, 142, 0.4);
}

.btn-primary:active:not(:disabled) {
  transform: translateY(0);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.btn-secondary {
  background: #ffffff;
  color: #6b7280;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 14px 28px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 48px;
}

.btn-secondary:hover:not(:disabled) {
  background: #f9fafb;
  border-color: #d1d5db;
  transform: translateY(-1px);
}

.btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

/* Form Actions */
.form-actions {
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  margin-top: 32px;
}

/* Loading Animation */
.loading-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Modern Button Spinner */
.button-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin-button 0.8s cubic-bezier(0.5, 0, 0.5, 1) infinite;
}

@keyframes spin-button {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .modal-overlay {
    padding-top: 24px;
    align-items: flex-start;
  }
  
  .modal-content {
    margin: 12px;
    width: calc(100% - 24px);
    max-height: calc(100vh - 48px);
  }

  .modal-header {
    padding: 20px 24px;
  }

  .form-container {
    padding: 16px;
  }

  .header-content {
    gap: 12px;
  }

  .modal-header h2 {
    font-size: 20px;
  }

  .form-grid {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .full-width {
    grid-column: span 1;
  }

  .form-actions {
    flex-direction: column;
    gap: 10px;
    margin-top: 20px;
  }

  .btn-primary,
  .btn-secondary {
    width: 100%;
  }

  .profile-picture-section {
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 14px 12px;
  }

  .current-picture {
    width: 84px;
    height: 84px;
  }

  .picture-meta {
    width: 100%;
    align-items: center;
  }

  .upload-link-btn {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .modal-overlay {
    padding-top: 12px;
  }
  
  .modal-content {
    width: calc(100% - 16px);
    margin: 8px;
    max-height: calc(100vh - 24px);
    border-radius: 12px;
  }
  
  .modal-header {
    padding: 16px 20px;
  }

  .form-container {
    padding: 14px;
  }

  .modal-header h2 {
    font-size: 18px;
  }

  .modal-header p {
    font-size: 12px;
  }

  .profile-picture-section {
    padding: 12px 10px;
  }

  .current-picture {
    width: 76px;
    height: 76px;
  }

  .picture-title {
    font-size: 12px;
  }

  .upload-link-btn {
    font-size: 11px;
    padding: 6px 8px;
  }
}

/* Toast Notification Styles */
.toast-container {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 10001;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 400px;
  pointer-events: none;
}

.toast {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  border-left: 4px solid transparent;
  min-width: 320px;
  pointer-events: auto;
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.toast-error {
  border-left-color: #ef4444;
  background: linear-gradient(135deg, #fef2f2, #ffffff);
}

.toast-success {
  border-left-color: #10b981;
  background: linear-gradient(135deg, #f0fdf4, #ffffff);
}

.toast-info {
  border-left-color: #3b82f6;
  background: linear-gradient(135deg, #eff6ff, #ffffff);
}

.toast-warning {
  border-left-color: #f59e0b;
  background: linear-gradient(135deg, #fffbeb, #ffffff);
}

.toast-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.toast-error .toast-icon {
  color: #ef4444;
}

.toast-success .toast-icon {
  color: #10b981;
}

.toast-info .toast-icon {
  color: #3b82f6;
}

.toast-warning .toast-icon {
  color: #f59e0b;
}

.toast-content {
  flex: 1;
  min-width: 0;
}

.toast-message {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  line-height: 1.4;
  word-wrap: break-word;
}

.toast-close {
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.toast-close:hover {
  color: #6b7280;
  background: rgba(0, 0, 0, 0.05);
  transform: scale(1.1);
}

/* Toast Animations */
.toast-enter-active {
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.toast-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 1, 1);
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(100%) scale(0.95);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(100%) scale(0.95);
}

.toast-move {
  transition: transform 0.3s ease;
}

/* Responsive Toast Styles */
@media (max-width: 768px) {
  .toast-container {
    top: 16px;
    right: 16px;
    left: 16px;
    max-width: none;
  }

  .toast {
    min-width: auto;
    width: 100%;
    padding: 14px 16px;
  }

  .toast-message {
    font-size: 14px;
  }
}

/* Focus and accessibility improvements */
.btn-primary:focus,
.btn-secondary:focus,
.close-btn:focus {
  outline: 2px solid #5b2e8e;
  outline-offset: 2px;
}

/* Smooth transitions for better UX */
* {
  box-sizing: border-box;
}

input,
button {
  font-family: inherit;
}
</style>
